import React, { useEffect, useMemo, useState } from 'react';
import adminService from '../../services/admin/admin.service';
import type { Template, HierarchyNode } from '../../types/admin/admin.types';
import { nodeLabel } from '../../utils/nodeLabel';

/**
 * Multi-select node-scope picker for role assignments (admin console).
 *
 * One checkbox list per filing-hierarchy LEVEL (Section → Sub-Section → …).
 * Any number of boxes can be checked at any level — checking a Section shows
 * its Sub-Sections (merged across every checked Section) as further
 * checkboxes. Every checked box, at any level, is a distinct scope; the
 * caller turns each one into its own role assignment. Leaving everything
 * unchecked means a single global (unscoped) assignment.
 *
 * `alreadyAssignedPaths` (materialized paths, e.g. "/12/45/", of the scopes
 * the selected role already has) are rendered pre-checked and locked, and
 * automatically expand their ancestor levels so a deep existing assignment
 * (e.g. a Sub-Section) is visible without the admin having to manually
 * re-check its parent Section first.
 *
 * Levels come from the admin master, so adding/renaming levels needs no code
 * change. Standalone value masters (File Category, KGID, …) are excluded —
 * scoping is only meaningful over the filing-hierarchy chain.
 */

/** Ordered filing-hierarchy chain, excluding standalone value masters. */
function buildChain(templates: Template[]): Template[] {
  const hierarchy = templates.filter((t) => t.is_hierarchy_node && t.is_active);
  const hasChildren = new Set(
    hierarchy.map((t) => t.parent_template_id).filter((v): v is number => v != null)
  );
  const chain = hierarchy.filter((t) => t.parent_template_id != null || hasChildren.has(t.id));
  const byId = new Map(chain.map((t) => [t.id, t]));
  const depthOf = (t: Template): number => {
    let depth = 0;
    let cur: Template | undefined = t;
    while (cur) {
      const pid = cur.parent_template_id;
      if (pid == null || !byId.has(pid)) break;
      cur = byId.get(pid);
      depth += 1;
      if (depth > chain.length) break; // cycle guard
    }
    return depth;
  };
  return [...chain].sort((a, b) => depthOf(a) - depthOf(b) || a.id - b.id);
}

function dedupeNodesById(nodes: HierarchyNode[]): HierarchyNode[] {
  const seen = new Set<number>();
  const out: HierarchyNode[] = [];
  for (const n of nodes) {
    if (seen.has(n.id)) continue;
    seen.add(n.id);
    out.push(n);
  }
  return out;
}

/** Materialized path "/12/45/" -> ["12", "45"] (root .. self). */
function pathSegments(path: string): string[] {
  return path.split('/').filter(Boolean);
}

// A stable reference for the "no already-assigned paths" default. A fresh
// `[]` literal as a default parameter value gets a new identity every render
// whenever the caller omits the prop (e.g. CreateUserModal's per-row picker),
// which feeds a useMemo and then a useEffect dependency below — a new
// identity every render means that effect never stops re-firing, flooding
// the API with requests in a runaway loop.
const NO_ALREADY_ASSIGNED_PATHS: string[] = [];

interface HierarchyScopePickerProps {
  /** Every user-checked node id, across every level ([] = global / no scope). Excludes already-assigned (locked) ids. */
  value: string[];
  onChange: (nodeIds: string[]) => void;
  /** Materialized paths of nodes already assigned to the role being edited. */
  alreadyAssignedPaths?: string[];
  /** Whether the role being edited already has a global (unscoped) assignment. */
  alreadyGlobal?: boolean;
}

const HierarchyScopePicker: React.FC<HierarchyScopePickerProps> = ({
  value,
  onChange,
  alreadyAssignedPaths = NO_ALREADY_ASSIGNED_PATHS,
  alreadyGlobal = false,
}) => {
  const [levels, setLevels] = useState<Template[]>([]);
  const [checkedByLevel, setCheckedByLevel] = useState<string[][]>([]);
  const [options, setOptions] = useState<Record<number, HierarchyNode[]>>({});
  const [loading, setLoading] = useState<Record<number, boolean>>({});

  const directlyAssignedIds = useMemo(
    () => new Set(alreadyAssignedPaths.map((p) => pathSegments(p).slice(-1)[0]).filter(Boolean)),
    [alreadyAssignedPaths]
  );

  // For each level i, the ancestor ids (at level i-1) whose children must be
  // fetched so an already-assigned descendant is visible without the admin
  // manually re-checking its parent first.
  const forcedParentsAtLevel = useMemo(() => {
    const byLevel: string[][] = [];
    for (const p of alreadyAssignedPaths) {
      const segs = pathSegments(p);
      for (let i = 1; i < segs.length; i += 1) {
        (byLevel[i] ||= []).push(segs[i - 1]);
      }
    }
    return byLevel;
  }, [alreadyAssignedPaths]);

  // Load the level definitions once.
  useEffect(() => {
    let cancelled = false;
    adminService
      .listTemplates({ is_active: true, is_hierarchy_node: true })
      .then((res) => {
        if (cancelled) return;
        const chain = buildChain(res.data);
        setLevels(chain);
        setCheckedByLevel(chain.map(() => []));
      })
      .catch(() => setLevels([]));
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch every level's options, cascading down using both the admin's own
  // checked parents and any parent forced open by an already-assigned
  // descendant, so pre-existing scoped assignments are always visible.
  useEffect(() => {
    if (levels.length === 0) return;
    let cancelled = false;
    (async () => {
      for (let i = 0; i < levels.length; i += 1) {
        if (cancelled) return;
        setLoading((l) => ({ ...l, [i]: true }));
        try {
          if (i === 0) {
            const res = await adminService.listNodes({ template_id: levels[0].id, is_active: true, limit: 100 });
            if (!cancelled) setOptions((o) => ({ ...o, 0: res.data }));
            continue;
          }
          const parents = Array.from(
            new Set([...(checkedByLevel[i - 1] || []), ...(forcedParentsAtLevel[i] || [])])
          );
          if (parents.length === 0) {
            if (!cancelled) setOptions((o) => ({ ...o, [i]: [] }));
            continue;
          }
          const perParent = await Promise.all(
            parents.map((pid) =>
              adminService
                .listNodes({ template_id: levels[i].id, is_active: true, limit: 100, parent_node_id: Number(pid) })
                .then((res) => res.data)
                .catch(() => [] as HierarchyNode[])
            )
          );
          if (!cancelled) setOptions((o) => ({ ...o, [i]: dedupeNodesById(perParent.flat()) }));
        } finally {
          if (!cancelled) setLoading((l) => ({ ...l, [i]: false }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levels, checkedByLevel, forcedParentsAtLevel]);

  // Report only the admin's own toggled selections upward (already-assigned
  // ids are locked and never enter checkedByLevel, so they're naturally
  // excluded — resubmitting an existing assignment would just 409).
  useEffect(() => {
    onChange(checkedByLevel.flat());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedByLevel]);

  // Reset when the parent clears the value (e.g. after adding assignments).
  useEffect(() => {
    if (value.length === 0 && checkedByLevel.some((c) => c.length > 0)) {
      setCheckedByLevel(levels.map(() => []));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const toggle = (levelIndex: number, nodeId: string, isChecked: boolean) => {
    setCheckedByLevel((prev) => {
      const nextForLevel = isChecked
        ? [...(prev[levelIndex] || []), nodeId]
        : (prev[levelIndex] || []).filter((v) => v !== nodeId);
      const next = prev.map((c, i) => (i === levelIndex ? nextForLevel : c));
      // Unchecking a parent invalidates deeper manual selections under it
      // (already-assigned descendants stay visible via forcedParentsAtLevel).
      for (let i = levelIndex + 1; i < next.length; i += 1) next[i] = [];
      return next;
    });
  };

  /** Check or clear every selectable (not already-locked) option at one level. */
  const toggleAllAtLevel = (levelIndex: number, selectableIds: string[], selectAll: boolean) => {
    setCheckedByLevel((prev) => {
      const next = prev.map((c, i) => (i === levelIndex ? (selectAll ? selectableIds : []) : c));
      for (let i = levelIndex + 1; i < next.length; i += 1) next[i] = [];
      return next;
    });
  };

  if (levels.length === 0) {
    return (
      <p className="text-xs text-[var(--color-gray-400)]">
        No filing hierarchy defined — the assignment will apply globally.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {alreadyGlobal && (
        <p className="text-xs text-[var(--color-success)]">
          This role is already assigned globally — it already covers every scope below.
        </p>
      )}
      {levels.map((tpl, i) => {
        const enabled = i === 0 || (checkedByLevel[i - 1] || []).length > 0 || (forcedParentsAtLevel[i] || []).length > 0;
        const opts = options[i] || [];
        const checked = new Set(checkedByLevel[i] || []);
        const selectableIds = opts.map((n) => String(n.id)).filter((id) => !directlyAssignedIds.has(id));
        const allSelected = selectableIds.length > 0 && selectableIds.every((id) => checked.has(id));
        return (
          <div key={tpl.id}>
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs text-[var(--color-gray-500)]">
                {tpl.name}
                {loading[i] && <span className="ml-1">(loading…)</span>}
              </div>
              {enabled && selectableIds.length > 1 && (
                <label className="flex items-center gap-1 text-xs text-[var(--color-primary)] cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => toggleAllAtLevel(i, selectableIds, e.target.checked)}
                  />
                  Select all
                </label>
              )}
            </div>
            {!enabled ? (
              <p className="text-xs text-[var(--color-gray-400)]">
                Check a {levels[i - 1]?.name.toLowerCase()} above to see its {tpl.name.toLowerCase()} options.
              </p>
            ) : opts.length === 0 && !loading[i] ? (
              <p className="text-xs text-[var(--color-gray-400)]">No {tpl.name.toLowerCase()} options.</p>
            ) : (
              <div className="flex flex-wrap gap-x-4 gap-y-1 max-h-36 overflow-y-auto rounded-[var(--radius-md)] border border-[var(--color-gray-200)] p-2">
                {opts.map((n) => {
                  const idStr = String(n.id);
                  const locked = directlyAssignedIds.has(idStr);
                  const isChecked = locked || checked.has(idStr);
                  return (
                    <label
                      key={n.id}
                      className={`flex items-center gap-1.5 text-sm cursor-pointer ${
                        locked ? 'text-[var(--color-gray-500)]' : 'text-[var(--color-gray-700)]'
                      }`}
                      title={locked ? 'Already assigned — remove it from Role assignments above to change.' : undefined}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        disabled={locked}
                        onChange={(e) => toggle(i, idStr, e.target.checked)}
                      />
                      {nodeLabel(n)}
                      {locked && <span className="text-[10px] text-[var(--color-success)] font-medium">✓ assigned</span>}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      {value.length === 0 && directlyAssignedIds.size === 0 && (
        <p className="text-xs text-[var(--color-gray-400)]">Nothing checked — the assignment will apply globally.</p>
      )}
    </div>
  );
};

export default HierarchyScopePicker;
